/**
 * 消息任务状态匹配与更新工具函数（P2-2 + P2-4）
 *
 * 抽出原先在 3 处重复的 `matchByMediaData || matchByMetadata` 匹配逻辑（P2-2）
 * 及 mediaData.status / metadata.taskStatus 更新逻辑（P2-4），统一为纯函数，
 * 消除 taskStore.ts（2 处）和 taskResultSync.ts（1 处）的重复代码。
 *
 * 设计为纯函数（不调用任何 store），避免 taskStore ↔ taskResultSync 循环依赖。
 * 调用方负责将返回的 newMessages 传给 setMessages。
 */
import type { Message } from '../types';

// ============================================================================
// P2-2: findMessageByTaskId
// ============================================================================

/**
 * 按 taskId 匹配消息。
 *
 * 匹配规则（与原 3 处 inline 逻辑一致）：
 *   - mediaData.taskId === taskId，或
 *   - metadata.pendingTaskId === taskId，或
 *   - metadata.batchTaskIds 数组包含 taskId（批量生成场景）
 *
 * @returns 匹配到的消息，或 undefined
 */
export function findMessageByTaskId(
  messages: Message[],
  taskId: string,
): Message | undefined {
  return messages.find(
    (m) =>
      (m.mediaData && m.mediaData.taskId === taskId) ||
      m.metadata?.pendingTaskId === taskId ||
      (Array.isArray(m.metadata?.batchTaskIds) &&
        (m.metadata.batchTaskIds as string[]).includes(taskId)),
  );
}

// ============================================================================
// P2-4: updateMessageForTaskStatus
// ============================================================================

/** 消息状态更新规格 */
export interface MessageStatusUpdate {
  /** mediaData.status 新值（必传） */
  status: string;
  /** metadata.taskStatus 新值（不传则不更新 taskStatus） */
  taskStatus?: string;
  /**
   * mediaData.results 新值。
   * - 不传（undefined）→ 不更新 results（保留原值）
   * - 传入但为空数组 → 回退到原 mediaData.results，再回退到 []
   * - 传入且非空 → 使用传入值
   */
  results?: any[];
  /** mediaData.videoMetadata 新值（不传则不更新） */
  videoMetadata?: any;
  /** mediaData.error 新值（不传则不更新） */
  error?: string;
  /** message.text 新值（不传则不更新 text） */
  text?: string;
  /**
   * 方案4.1 修复（2026-08-05）：task 快照更新。
   * 任务完成后需同步更新 message.task，避免 TaskCard 显示过时的确认文本。
   * - 不传（undefined）→ 不更新 task
   * - 传入对象 → 合并到原 task（浅合并）
   */
  task?: Partial<import('../types').Message['task']>;
}

/** updateMessageForTaskStatus 返回值 */
export interface MessageStatusUpdateResult {
  /** 更新后的消息数组（长度与入参相同） */
  messages: Message[];
  /** 匹配到的消息 ID（未匹配则为 undefined） */
  matchedMsgId?: string;
  /** 匹配到的消息更新后的 metadata（未匹配则为 undefined） */
  matchedMetadata?: Record<string, any>;
}

/**
 * 按 taskId 批量更新消息的媒体状态。
 *
 * 统一 3 处重复的 setMessages + matchByMediaData/matchByMetadata + mediaData 更新逻辑：
 *   1. taskStore.ts 中间态（queued/running）— 仅更新 status + taskStatus
 *   2. taskStore.ts final 态无 task 记录 — 更新 status + results + videoMetadata + error + text + taskStatus
 *   3. taskResultSync.ts final 态 — 同 2，额外需要 matchedMsgId 做后端同步
 *
 * 使用方式：
 * ```ts
 * // 简单场景（不需要后端同步）
 * setMessages((prev) => updateMessageForTaskStatus(prev, taskId, update).messages);
 *
 * // 需要后端同步
 * let matchedMsgId: string | undefined;
 * setMessages((prev) => {
 *   const r = updateMessageForTaskStatus(prev, taskId, update);
 *   matchedMsgId = r.matchedMsgId;
 *   return r.messages;
 * });
 * if (matchedMsgId) { /* 同步到后端 *\/ }
 * ```
 */
export function updateMessageForTaskStatus(
  messages: Message[],
  taskId: string,
  update: MessageStatusUpdate,
): MessageStatusUpdateResult {
  let matchedMsgId: string | undefined;
  let matchedMetadata: Record<string, any> | undefined;

  const newMessages = messages.map((m) => {
    const matchByMediaData = m.mediaData && m.mediaData.taskId === taskId;
    const matchByMetadata = m.metadata?.pendingTaskId === taskId;
    // 🔴 批量任务匹配：通过 metadata.batchTaskIds 数组匹配批量子任务
    const matchByBatchTaskIds =
      Array.isArray(m.metadata?.batchTaskIds) &&
      (m.metadata.batchTaskIds as string[]).includes(taskId);
    if (!matchByMediaData && !matchByMetadata && !matchByBatchTaskIds) return m;

    matchedMsgId = m.id;
    // 🔴 5.1 方案① 修复：批量任务统一追加模式。
    //   原判定 `matchByBatchTaskIds && !matchByMediaData && !matchByMetadata` 会让第一个子任务
    //   （其 task_id 同时存在于 mediaData.taskId / pendingTaskId / batchTaskIds 三处）走"替换"分支，
    //   在它完成时覆盖之前已累积的其他子任务结果。改为只要命中 batchTaskIds 即强制追加，
    //   消除"第一个子任务最后完成导致面板结果互相覆盖"的 bug。
    //   非批量场景 batchTaskIds 不存在，isBatchAppend 本就为 false，无副作用。
    const isBatchAppend = matchByBatchTaskIds;
    const newMetadata =
      update.taskStatus !== undefined
        ? { ...m.metadata, taskStatus: update.taskStatus }
        : m.metadata;
    matchedMetadata = newMetadata;

    // mediaData 更新：仅当原 mediaData 存在时更新（不存在则保留 undefined）
    const newMediaData = m.mediaData
      ? {
          ...m.mediaData,
          // 🔴 批量追加模式：单个子任务成功时保持 RUNNING 状态，
          //   直到所有批量子任务完成（由 batchTaskTracker 触发 stage2 过渡消息处理最终状态）
          status: isBatchAppend && update.status === 'SUCCESS'
            ? 'RUNNING'
            : update.status,
          // results: 批量追加模式 → 追加到现有结果；普通模式 → 替换（原逻辑）
          ...(update.results !== undefined
            ? {
                results: isBatchAppend
                  ? [...(m.mediaData?.results || []), ...update.results]
                  : update.results.length > 0
                    ? update.results
                    : (m.mediaData?.results || []),
              }
            : {}),
          ...(update.videoMetadata !== undefined
            ? { videoMetadata: update.videoMetadata }
            : {}),
          ...(update.error !== undefined ? { error: update.error } : {}),
        }
      : m.mediaData;

    // 方案A：批量场景更新 batchItems 中对应元素的状态（生成中进度列表实时更新）
    if (isBatchAppend && newMediaData && Array.isArray(newMediaData.batchItems)) {
      newMediaData.batchItems = newMediaData.batchItems.map((bi: any) =>
        bi.taskId === taskId
          ? { ...bi, status: update.status === 'SUCCESS' ? 'SUCCESS' : (update.status === 'FAILED' || update.status === 'ERROR' ? 'FAILED' : bi.status) }
          : bi
      );
    }

    // 方案4.1 修复（2026-08-05）：task 快照更新
    // 任务完成后更新 task.status / task.content，避免 TaskCard 显示过时的确认文本
    let newTask = m.task;
    if (update.task && m.task) {
      newTask = { ...m.task, ...update.task };
    }

    return {
      ...m,
      ...(newMediaData !== m.mediaData ? { mediaData: newMediaData } : {}),
      ...(update.text !== undefined ? { text: update.text } : {}),
      ...(newTask !== m.task ? { task: newTask } : {}),
      metadata: newMetadata,
    };
  });

  return { messages: newMessages, matchedMsgId, matchedMetadata };
}
